import { useEffect, useRef } from 'react'
import { useSession } from '@/context/SessionProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { useAppModal, AppModals } from '@/context/ModalProvider'
import { USDC_DENOM, USDT_DENOM } from '@/constants/constants'
import { getTokens } from '@/services/token-service'
import { TokenMoney } from '@ninjablaze/sdk-ts'

const CONVERSION_PROMPT_KEY = 'usdc-conversion-prompted'

// Minimum amount for conversion: 1.0 USDC (in chain format with 6 decimals)
const MIN_CONVERSION_AMOUNT = 1000000n // 1 USDC = 1,000,000 micro units

// Helper to round quantity to tick size (0.01 USDC)
const roundToTickSize = (chainValue: string): bigint => {
  const STEP_SIZE = 10000n // 0.01 USDC = 10,000 micro units (step)
  const chainValueBN = BigInt(chainValue)

  // Round down to 0.01 USDC increments
  const rounded = (chainValueBN / STEP_SIZE) * STEP_SIZE
  return rounded
}

export const UsdcConversionChecker = () => {
  const { account } = useSession()
  const balance = useBalanceStore((state) => state.balance)
  const isLoading = useBalanceStore((state) => state.isLoading)
  const modal = useAppModal()
  const hasChecked = useRef(false)

  useEffect(() => {
    // Only check once when balances are loaded
    if (!account || isLoading || hasChecked.current) {
      return
    }

    const oldUsdcBalance =
      (balance[USDC_DENOM]?.bank && new TokenMoney(balance[USDC_DENOM].bank.chainValue, USDC_DENOM)) ||
      new TokenMoney(0, USDC_DENOM)
    const usdtBalance = USDT_DENOM
      ? (balance[USDT_DENOM]?.bank && new TokenMoney(balance[USDT_DENOM].bank.chainValue, USDC_DENOM)) ||
        new TokenMoney(0, USDC_DENOM)
      : new TokenMoney(0, USDC_DENOM)

    // Round balances to 0.01 USDC step and check if they meet minimum (1.0 USDC)
    const roundedOldUsdc = roundToTickSize(oldUsdcBalance.chainValue.toFixed(0))
    const roundedUsdt = roundToTickSize(usdtBalance.chainValue.toFixed(0))

    const hasOldUsdc = roundedOldUsdc >= MIN_CONVERSION_AMOUNT
    const hasUsdt = USDT_DENOM && roundedUsdt >= MIN_CONVERSION_AMOUNT

    // Check if user has any old USDC or USDT after rounding
    const hasOldTokens = hasOldUsdc || hasUsdt

    if (!hasOldTokens) {
      hasChecked.current = true
      return
    }

    // Get all token denoms except old USDC and USDT
    const allTokens = getTokens()
    const otherTokenBalances = allTokens
      .filter((token) => token.denom !== USDC_DENOM && token.denom !== USDT_DENOM)
      .map((token) => balance[token.denom]?.bank?.humanNumber || 0)

    const hasOtherTokens = otherTokenBalances.some((b) => b > 0.01)

    // If user ONLY has old USDC/USDT and no other tokens, auto-convert
    if (!hasOtherTokens && hasOldTokens) {
      modal.openModalNew(AppModals.UsdcConversion, { autoConvert: false })
      hasChecked.current = true
      return
    }

    // If user has old tokens AND other tokens, show the conversion modal
    if (hasOldTokens) {
      // Check if we've already prompted in this session
      const prompted = sessionStorage.getItem(CONVERSION_PROMPT_KEY)
      if (!prompted || true) {
        modal.openModalNew(AppModals.UsdcConversion, { autoConvert: false })
        sessionStorage.setItem(CONVERSION_PROMPT_KEY, 'true')
      }
      hasChecked.current = true
    }
  }, [account, balance, isLoading, modal])

  return null
}

// Helper function to reset the dismissal (for testing or if user wants to see it again)
export const resetUsdcConversionPrompt = () => {
  sessionStorage.removeItem(CONVERSION_PROMPT_KEY)
}
